import React, {FC, useEffect, useId, useState} from 'react';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import {ISnackbarState} from 'store/models/common';
import {useUpdateEffect} from 'hooks';
import {ErrorMessage} from '@hookform/error-message';
import {backupPeriodTypes} from 'core/constants';
import {useEditManifestMutation} from 'store/slices/manifests';
import {IGetManifestByIdData} from 'store/models/manifest';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControlLabel,
  FormGroup,
  Modal,
  Paper,
  Snackbar,
  Stack,
  styled,
  Switch,
  TextField,
} from '@mui/material';
import ShouldSaveChanges from '../ShouldSaveChanges/ShouldSaveChanges';
import {
  CloseIcon,
  CreateIcon,
  ErrorNotification,
  SelectPopupIcon,
  SuccessNotification,
} from 'assets/icons';
import {modal} from 'styles/MUIStyles/modal';
import {
  androidSwitch,
  createTaskBackupSwitchLabel,
  searchAndSelect,
  searchAndSelectError,
} from 'styles/MUIStyles/common';
import {
  addDirectoryButton,
  cancelButton,
  createTaskBackupButtons,
  warningModalButton,
} from 'styles/MUIStyles/buttons';
import {
  snackbarAlertContent,
  snackbarErrorAlert,
  snackbarSuccessAlert,
} from 'styles/MUIStyles/snackbars';
import styles from '../../../Stations/StationsEdit/StationsEdit.module.scss';

const AndroidSwitch = styled(Switch)(() => androidSwitch);

interface IManifestEditTask {
  name: string;
  id: string;
  isMain: boolean;
  include: {
    directory: string;
  }[];
  exclude:
    | {
        directory: string;
      }[]
    | null;
  isIncrementalBackupSettingsChecked: boolean;
  isExcludeDirectoryChecked: boolean;
  keepFullCopy: number;
  isPreBackupScriptChecked: boolean;
  isPostBackupScriptChecked: boolean;
  preBackupScripts: {script: string}[] | null;
  postBackupScripts: {script: string}[] | null;
  fullBackupSettings: {
    periodType: string | null;
    periodCount: number;
  } | null;
  incrementalBackupSettings: {
    periodType: string | null;
    periodCount: number;
  } | null;
}

interface Props {
  manifestData: IGetManifestByIdData | null;
  clickedManifestTask: IManifestEditTask | null;
  isEditTaskModalOpen: boolean;
  setIsEditTaskModalOpen: (isCreateTaskModalOpen: boolean) => void;
}

const ManifestEditTask: FC<Props> = (props) => {
  const {register, handleSubmit, watch, control, setValue, reset, formState} =
    useForm<IManifestEditTask>({
      defaultValues: {
        name: '',
        id: '',
        include: [{directory: ''}],
        exclude: [{directory: ''}],
        isIncrementalBackupSettingsChecked: false,
        isExcludeDirectoryChecked: false,
        keepFullCopy: 1,
        isPreBackupScriptChecked: false,
        isPostBackupScriptChecked: false,
        preBackupScripts: [{script: ''}],
        postBackupScripts: [{script: ''}],
        fullBackupSettings: {
          periodType: null,
          periodCount: 1,
        },
        incrementalBackupSettings: {
          periodType: null,
          periodCount: 1,
        },
      },
    });

  const {
    fields: includeDirectories,
    append: appendIncludeDirectory,
    remove: deleteIncludeDirectory,
  } = useFieldArray<IManifestEditTask>({
    control,
    name: 'include',
  });

  const {
    fields: excludeDirectories,
    append: appendExcludeDirectory,
    remove: deleteExcludeDirectory,
  } = useFieldArray<IManifestEditTask>({
    control,
    name: 'exclude',
  });

  const {
    fields: preScripts,
    append: appendPreScript,
    remove: deletePreScript,
  } = useFieldArray<IManifestEditTask>({
    control,
    name: 'preBackupScripts',
  });

  const {
    fields: postScripts,
    append: appendPostScript,
    remove: deletePostScript,
  } = useFieldArray<IManifestEditTask>({
    control,
    name: 'postBackupScripts',
  });

  const fullBackupSettingsPeriodCount = watch('fullBackupSettings.periodCount');
  const incrementalBackupSettingsPeriodCount = watch(
    'incrementalBackupSettings.periodCount',
  );
  const keepFullBackupCopyCount = watch('keepFullCopy');
  const isIncrementalBackupSettingsChecked = watch(
    'isIncrementalBackupSettingsChecked',
  );
  const isExcludeDirectoryChecked = watch('isExcludeDirectoryChecked');
  const isPreBackupScriptChecked = watch('isPreBackupScriptChecked');
  const isPostBackupScriptChecked = watch('isPostBackupScriptChecked');

  const [
    editManifest,
    {
      isLoading: isEditingManifestLoading,
      isSuccess: isEditingManifestSuccess,
      error: editManifestError,
    },
  ] = useEditManifestMutation();

  const editManifestTaskFormId = useId();
  const [editManifestTaskErrorMessage, setEditManifestTaskErrorMessage] =
    useState<null | string>(null);
  const [shouldSaveChangesModalOpen, setShouldSaveChangesModalOpen] =
    useState(false);
  const [editManifestTaskSnackbarSuccess, setEditManifestTaskSnackbarSuccess] =
    useState<ISnackbarState>({
      open: false,
      vertical: 'top',
      horizontal: 'center',
    });

  const [editManifestTaskSnackbarError, setEditManifestTaskSnackbarError] =
    useState<ISnackbarState>({
      open: false,
      vertical: 'top',
      horizontal: 'center',
    });

  const {
    vertical: editManifestTaskSuccessVertical,
    horizontal: editManifestTaskSuccessHorizontal,
    open: isEditManifestTaskSuccessOpen,
  } = editManifestTaskSnackbarSuccess;
  const {
    vertical: editManifestTaskErrorVertical,
    horizontal: editManifestTaskErrorHorizontal,
    open: isEditManifestTaskErrorOpen,
  } = editManifestTaskSnackbarError;

  const handleSnackbarClose = () => {
    setEditManifestTaskSnackbarSuccess({
      ...editManifestTaskSnackbarSuccess,
      open: false,
    });
    setEditManifestTaskSnackbarError({
      ...editManifestTaskSnackbarError,
      open: false,
    });
    setEditManifestTaskErrorMessage(null);
  };

  const onSubmit = (formData: IManifestEditTask) => {
    if (props.manifestData) {
      const editableManifestTask = props.manifestData.commands.find(
        (command) => command.id === formData.id,
      );

      if (editableManifestTask) {
        editManifest({
          id: props.manifestData.id,
          commands: [
            ...props.manifestData.commands.map((command) => {
              if (command.id === editableManifestTask.id) {
                return {
                  name: formData.name,
                  id: formData.id,
                  isMain: formData.isMain,
                  include: [...formData.include.map((path) => path.directory)],
                  exclude: formData.exclude?.[0]?.directory
                    ? [...formData.exclude.map((path) => path.directory)]
                    : null,
                  preBackupScripts: formData.preBackupScripts?.[0]?.script
                    ? [...formData.preBackupScripts.map((path) => path.script)]
                    : null,
                  postBackupScripts: formData.postBackupScripts?.[0]?.script
                    ? [...formData.postBackupScripts.map((path) => path.script)]
                    : null,
                  fullBackupSettings: formData.fullBackupSettings,
                  keepFullCopy: formData.keepFullCopy,
                  incrementalBackupSettings: formData?.incrementalBackupSettings
                    ?.periodType
                    ? formData.incrementalBackupSettings
                    : null,
                };
              }

              return command;
            }),
          ],
        });
      }
    }
  };

  useUpdateEffect(() => {
    if (!isIncrementalBackupSettingsChecked) {
      setValue('incrementalBackupSettings', {
        periodType: null,
        periodCount: 1,
      });
    }

    if (!isExcludeDirectoryChecked) {
      setValue('exclude', [{directory: ''}]);
    }

    if (!isPreBackupScriptChecked) {
      setValue('preBackupScripts', [{script: ''}]);
    }

    if (!isPostBackupScriptChecked) {
      setValue('postBackupScripts', [{script: ''}]);
    }
  }, [
    isIncrementalBackupSettingsChecked,
    isExcludeDirectoryChecked,
    isPreBackupScriptChecked,
    isPostBackupScriptChecked,
  ]);

  useUpdateEffect(() => {
    if (isEditingManifestSuccess) {
      setEditManifestTaskSnackbarSuccess({
        vertical: 'top',
        horizontal: 'center',
        open: true,
      });
      props.setIsEditTaskModalOpen(false);
      setShouldSaveChangesModalOpen(false);
    }
  }, [isEditingManifestSuccess]);

  useUpdateEffect(() => {
    if (
      editManifestError &&
      'data' in editManifestError &&
      'status' in editManifestError
    ) {
      if (
        editManifestError.status === 400 ||
        editManifestError.status === 404
      ) {
        const errors = editManifestError.data?.errors;

        for (let key in errors) {
          if (Array.isArray(errors[key])) {
            setEditManifestTaskErrorMessage(errors[key]);
            setEditManifestTaskSnackbarError({
              vertical: 'top',
              horizontal: 'center',
              open: true,
            });
            break;
          }
        }
      }
    }
  }, [editManifestError]);

  useEffect(() => {
    if (Object.keys(formState.errors).length) {
      setShouldSaveChangesModalOpen(false);
    }
  }, [formState]);

  useEffect(() => {
    if (props.clickedManifestTask) {
      reset({
        name: props.clickedManifestTask.name,
        id: props.clickedManifestTask.id,
        include: props.clickedManifestTask.include,
        isMain: props.clickedManifestTask.isMain,
        exclude: props.clickedManifestTask?.exclude?.length
          ? props.clickedManifestTask.exclude
          : [{directory: ''}],
        preBackupScripts: props.clickedManifestTask?.preBackupScripts?.length
          ? props.clickedManifestTask.preBackupScripts
          : [{script: ''}],
        postBackupScripts: props.clickedManifestTask?.postBackupScripts?.length
          ? props.clickedManifestTask.postBackupScripts
          : [{script: ''}],
        isIncrementalBackupSettingsChecked:
          props.clickedManifestTask.isIncrementalBackupSettingsChecked,
        isExcludeDirectoryChecked:
          props.clickedManifestTask.isExcludeDirectoryChecked,
        isPreBackupScriptChecked:
          props.clickedManifestTask.isPreBackupScriptChecked,
        isPostBackupScriptChecked:
          props.clickedManifestTask.isPostBackupScriptChecked,
        fullBackupSettings: props.clickedManifestTask.fullBackupSettings,
        incrementalBackupSettings:
          props.clickedManifestTask.incrementalBackupSettings,
        keepFullCopy: props.clickedManifestTask.keepFullCopy,
      });
    }
  }, [props.clickedManifestTask, reset]);

  return (
    <>
      <Modal
        open={props.isEditTaskModalOpen}
        onClose={() => props.setIsEditTaskModalOpen(false)}
      >
        <Box
          sx={{
            ...modal,
            visibility: shouldSaveChangesModalOpen ? 'hidden' : 'visible',
          }}
        >
          <form
            onSubmit={(event) => {
              event.stopPropagation();
              handleSubmit(onSubmit)(event);
            }}
            id={editManifestTaskFormId}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8.5px',
              }}
            >
              <h2 className={styles.createManifestTask}>Edit task</h2>
              <div
                className={styles.closeIcon}
                onClick={() => props.setIsEditTaskModalOpen(false)}
              >
                <CloseIcon />
              </div>
            </Box>
            <div
              className={
                styles.nameInputContainer + ' ' + styles.createTaskName
              }
            >
              <label htmlFor="name">Name</label>
              <input
                {...register('name', {required: 'This field is required'})}
                type="text"
                id="name"
                autoComplete="off"
                className={`${
                  formState.errors?.name ? 'input inputError' : 'input'
                }`}
                placeholder="Enter name"
              />
              <ErrorMessage
                name="name"
                errors={formState.errors}
                render={({message}) => (
                  <span className={styles.inputErrorMessage}>{message}</span>
                )}
              />
            </div>
            <h3 className={styles.createManifestCommands}>Commands:</h3>
            {includeDirectories.map((includeDirectory, index) => (
              <div
                className={
                  styles.includeInputContainer + ' ' + styles.includeDirectory
                }
                key={includeDirectory.id}
              >
                {index === 0 && <label htmlFor="include">Include</label>}
                <div className={styles.createAndDeleteDirectoryContainer}>
                  <input
                    {...register(`include.${index}.directory`, {
                      required: 'This field is required',
                    })}
                    type="text"
                    id="include"
                    autoComplete="off"
                    className={`${
                      formState.errors?.include?.[index]?.directory
                        ? 'input inputError'
                        : 'input'
                    }`}
                    placeholder="Write directories"
                  />
                  {index === 0 && (
                    <Button
                      type="button"
                      sx={addDirectoryButton}
                      onClick={() => {
                        appendIncludeDirectory({directory: ''});
                      }}
                    >
                      <CreateIcon />
                    </Button>
                  )}
                  {index > 0 && (
                    <Button
                      type="button"
                      sx={{
                        color: 'var(--primary)',
                        borderRadius: '4px',
                        minWidth: '40px',
                        minHeight: '40px',
                      }}
                      onClick={() => {
                        deleteIncludeDirectory(index);
                      }}
                    >
                      <span className={styles.deleteDirectory} />
                    </Button>
                  )}
                </div>
                <ErrorMessage
                  name={`include.${index}.directory`}
                  errors={formState.errors}
                  render={({message}) => (
                    <span className={styles.inputErrorMessage}>{message}</span>
                  )}
                />
              </div>
            ))}

            {!props.clickedManifestTask?.isMain ? (
              <div className={styles.createTaskBackupFull}>
                <span>Backup-full</span>
                <div className={styles.createTaskFullBackupControls}>
                  <span>Every</span>
                  <div className={styles.fullBackupButtonsContainer}>
                    <Button
                      disabled={fullBackupSettingsPeriodCount === 1}
                      sx={createTaskBackupButtons}
                      onClick={() => {
                        if (fullBackupSettingsPeriodCount > 1) {
                          setValue(
                            'fullBackupSettings.periodCount',
                            fullBackupSettingsPeriodCount - 1,
                          );
                        }
                      }}
                    >
                      -
                    </Button>
                    <div className={styles.fullBackupSettingsPeriodCount}>
                      {fullBackupSettingsPeriodCount}
                    </div>
                    <Button
                      disabled={fullBackupSettingsPeriodCount === 15}
                      sx={createTaskBackupButtons}
                      onClick={() => {
                        if (fullBackupSettingsPeriodCount < 15) {
                          setValue(
                            'fullBackupSettings.periodCount',
                            fullBackupSettingsPeriodCount + 1,
                          );
                        }
                      }}
                    >
                      +
                    </Button>
                  </div>
                  <Controller
                    name={'fullBackupSettings.periodType'}
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!value) {
                          return 'This field cannot be empty';
                        }
                      },
                    }}
                    render={({field}) => (
                      <Autocomplete
                        {...field}
                        options={backupPeriodTypes}
                        value={field.value}
                        onChange={(_, data) => field.onChange(data)}
                        PaperComponent={(props) => (
                          <Paper
                            sx={{
                              borderRadius: '12px',
                              boxShadow: 'var(--shadow-card)',
                              marginTop: '8px',
                              cursor: 'pointer',
                            }}
                            {...props}
                          />
                        )}
                        sx={{width: '20%', flexGrow: 2}}
                        ListboxProps={{
                          style: {maxHeight: '200px'},
                          className: 'autoCompleteListBox',
                        }}
                        isOptionEqualToValue={(option, value) =>
                          value === '' ? true : option === value
                        }
                        popupIcon={<SelectPopupIcon />}
                        renderOption={(props: any, option) => {
                          return (
                            <span {...props} className={styles.dropDownItem}>
                              {option}
                            </span>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Select time period"
                            sx={
                              formState.errors?.fullBackupSettings
                                ? searchAndSelectError
                                : {...searchAndSelect}
                            }
                          />
                        )}
                      />
                    )}
                  />
                </div>
                <ErrorMessage
                  errors={formState.errors}
                  name={'fullBackupSettings.periodType'}
                  render={({message}) => (
                    <span className={styles.inputErrorMessage}>{message}</span>
                  )}
                />
              </div>
            ) : null}

            {!props.clickedManifestTask?.isMain ? (
              <div className={styles.createTaskBackupKeepFullCopy}>
                <div className={styles.createTaskBackupKeepFullCopyControls}>
                  <span>Keep</span>
                  <div className={styles.fullBackupButtonsContainer}>
                    <Button
                      disabled={keepFullBackupCopyCount === 1}
                      sx={createTaskBackupButtons}
                      onClick={() => {
                        if (keepFullBackupCopyCount > 1) {
                          setValue('keepFullCopy', keepFullBackupCopyCount - 1);
                        }
                      }}
                    >
                      -
                    </Button>
                    <div className={styles.fullBackupSettingsPeriodCount}>
                      {keepFullBackupCopyCount}
                    </div>
                    <Button
                      disabled={keepFullBackupCopyCount === 10}
                      sx={createTaskBackupButtons}
                      onClick={() => {
                        if (keepFullBackupCopyCount < 10) {
                          setValue('keepFullCopy', keepFullBackupCopyCount + 1);
                        }
                      }}
                    >
                      +
                    </Button>
                  </div>
                  <span style={{width: '20%', flexGrow: '2'}}>full copy</span>
                </div>
              </div>
            ) : null}

            {!props.clickedManifestTask?.isMain ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Controller
                  name={'isIncrementalBackupSettingsChecked'}
                  control={control}
                  render={({field}) => (
                    <FormGroup>
                      <FormControlLabel
                        sx={createTaskBackupSwitchLabel}
                        control={
                          <AndroidSwitch {...field} checked={field.value} />
                        }
                        label={'Backup-incremental'}
                      />
                    </FormGroup>
                  )}
                />
              </Stack>
            ) : null}

            {!props.clickedManifestTask?.isMain &&
            isIncrementalBackupSettingsChecked ? (
              <div className={styles.createTaskBackupIncremental}>
                <div className={styles.createTaskIncrementalBackupControls}>
                  <span>Every</span>
                  <div className={styles.incrementalBackupButtonsContainer}>
                    <Button
                      disabled={incrementalBackupSettingsPeriodCount === 1}
                      sx={createTaskBackupButtons}
                      onClick={() => {
                        if (incrementalBackupSettingsPeriodCount > 1) {
                          setValue(
                            'incrementalBackupSettings.periodCount',
                            incrementalBackupSettingsPeriodCount - 1,
                          );
                        }
                      }}
                    >
                      -
                    </Button>
                    <div className={styles.fullBackupSettingsPeriodCount}>
                      {incrementalBackupSettingsPeriodCount}
                    </div>
                    <Button
                      disabled={incrementalBackupSettingsPeriodCount === 15}
                      sx={createTaskBackupButtons}
                      onClick={() => {
                        if (incrementalBackupSettingsPeriodCount < 15) {
                          setValue(
                            'incrementalBackupSettings.periodCount',
                            incrementalBackupSettingsPeriodCount + 1,
                          );
                        }
                      }}
                    >
                      +
                    </Button>
                  </div>
                  <Controller
                    name={'incrementalBackupSettings.periodType'}
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (!value && isIncrementalBackupSettingsChecked) {
                          return 'This field is required';
                        }
                      },
                    }}
                    render={({field}) => (
                      <Autocomplete
                        {...field}
                        options={backupPeriodTypes}
                        value={field.value}
                        onChange={(_, data) => field.onChange(data)}
                        PaperComponent={(props) => (
                          <Paper
                            sx={{
                              borderRadius: '12px',
                              boxShadow: 'var(--shadow-card)',
                              marginTop: '8px',
                              cursor: 'pointer',
                            }}
                            {...props}
                          />
                        )}
                        sx={{width: '20%', flexGrow: 2}}
                        ListboxProps={{
                          style: {maxHeight: '200px'},
                          className: 'autoCompleteListBox',
                        }}
                        isOptionEqualToValue={(option, value) =>
                          value === '' ? true : option === value
                        }
                        popupIcon={<SelectPopupIcon />}
                        renderOption={(props: any, option) => {
                          return (
                            <span {...props} className={styles.dropDownItem}>
                              {option}
                            </span>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder="Select time period"
                            sx={
                              formState.errors?.incrementalBackupSettings
                                ? searchAndSelectError
                                : {...searchAndSelect}
                            }
                          />
                        )}
                      />
                    )}
                  />
                </div>
                <ErrorMessage
                  errors={formState.errors}
                  name={'incrementalBackupSettings.periodType'}
                  render={({message}) => (
                    <span className={styles.inputErrorMessage}>{message}</span>
                  )}
                />
              </div>
            ) : null}

            <Stack direction="row" spacing={1} alignItems="center">
              <Controller
                name={'isExcludeDirectoryChecked'}
                control={control}
                render={({field}) => (
                  <FormGroup>
                    <FormControlLabel
                      sx={createTaskBackupSwitchLabel}
                      control={
                        <AndroidSwitch {...field} checked={field.value} />
                      }
                      label={'Exclude'}
                    />
                  </FormGroup>
                )}
              />
            </Stack>

            {isExcludeDirectoryChecked ? (
              <>
                {excludeDirectories.map((excludeDirectory, index) => (
                  <div
                    className={
                      styles.includeInputContainer +
                      ' ' +
                      styles.includeDirectory
                    }
                    key={excludeDirectory.id}
                  >
                    <div className={styles.createAndDeleteDirectoryContainer}>
                      <input
                        {...register(`exclude.${index}.directory`, {
                          required: isExcludeDirectoryChecked
                            ? 'This field is required'
                            : false,
                        })}
                        type="text"
                        autoComplete="off"
                        className={`${
                          formState.errors?.exclude?.[index]?.directory
                            ? 'input inputError'
                            : 'input'
                        }`}
                        placeholder="Write directories"
                      />
                      {index === 0 && (
                        <Button
                          type="button"
                          sx={addDirectoryButton}
                          onClick={() => {
                            appendExcludeDirectory({directory: ''});
                          }}
                        >
                          <CreateIcon />
                        </Button>
                      )}
                      {index > 0 && (
                        <Button
                          type="button"
                          sx={{
                            color: 'var(--primary)',
                            borderRadius: '4px',
                            minWidth: '40px',
                            minHeight: '40px',
                          }}
                          onClick={() => {
                            deleteExcludeDirectory(index);
                          }}
                        >
                          <span className={styles.deleteDirectory} />
                        </Button>
                      )}
                    </div>
                    <ErrorMessage
                      name={`exclude.${index}.directory`}
                      errors={formState.errors}
                      render={({message}) => (
                        <span className={styles.inputErrorMessage}>
                          {message}
                        </span>
                      )}
                    />
                  </div>
                ))}
              </>
            ) : null}

            {!props.clickedManifestTask?.isMain ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Controller
                  name={'isPreBackupScriptChecked'}
                  control={control}
                  render={({field}) => (
                    <FormGroup>
                      <FormControlLabel
                        sx={createTaskBackupSwitchLabel}
                        control={
                          <AndroidSwitch {...field} checked={field.value} />
                        }
                        label={'Pre-backup scripts'}
                      />
                    </FormGroup>
                  )}
                />
              </Stack>
            ) : null}

            {!props.clickedManifestTask?.isMain && isPreBackupScriptChecked ? (
              <>
                {preScripts.map((preScript, index) => (
                  <div
                    className={styles.preScriptInputContainer}
                    key={preScript.id}
                  >
                    <div className={styles.createAndDeleteScriptContainer}>
                      <textarea
                        {...register(`preBackupScripts.${index}.script`)}
                        cols={30}
                        rows={2}
                        className={`${
                          formState.errors?.preBackupScripts?.[index]?.script
                            ? 'input inputError'
                            : 'input'
                        }`}
                        placeholder="Write script"
                      />
                      {index === 0 && (
                        <Button
                          type="button"
                          sx={addDirectoryButton}
                          onClick={() => {
                            appendPreScript({script: ''});
                          }}
                        >
                          <CreateIcon />
                        </Button>
                      )}
                      {index > 0 && (
                        <Button
                          type="button"
                          sx={{
                            color: 'var(--primary)',
                            borderRadius: '4px',
                            minWidth: '40px',
                            minHeight: '40px',
                          }}
                          onClick={() => {
                            deletePreScript(index);
                          }}
                        >
                          <span className={styles.deleteScript} />
                        </Button>
                      )}
                    </div>
                    <ErrorMessage
                      name={`preBackupScripts.${index}.script`}
                      errors={formState.errors}
                      render={({message}) => (
                        <span className={styles.inputErrorMessage}>
                          {message}
                        </span>
                      )}
                    />
                  </div>
                ))}
              </>
            ) : null}

            {!props.clickedManifestTask?.isMain ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Controller
                  name={'isPostBackupScriptChecked'}
                  control={control}
                  render={({field}) => (
                    <FormGroup>
                      <FormControlLabel
                        sx={createTaskBackupSwitchLabel}
                        control={
                          <AndroidSwitch {...field} checked={field.value} />
                        }
                        label={'Post-backup scripts'}
                      />
                    </FormGroup>
                  )}
                />
              </Stack>
            ) : null}

            {!props.clickedManifestTask?.isMain && isPostBackupScriptChecked ? (
              <>
                {postScripts.map((postScript, index) => (
                  <div
                    className={styles.postScriptInputContainer}
                    key={postScript.id}
                  >
                    <div className={styles.createAndDeleteScriptContainer}>
                      <textarea
                        {...register(`postBackupScripts.${index}.script`)}
                        cols={30}
                        rows={2}
                        className={`${
                          formState.errors?.postBackupScripts?.[index]?.script
                            ? 'input inputError'
                            : 'input'
                        }`}
                        placeholder="Write script"
                      />
                      {index === 0 && (
                        <Button
                          type="button"
                          sx={addDirectoryButton}
                          onClick={() => {
                            appendPostScript({script: ''});
                          }}
                        >
                          <CreateIcon />
                        </Button>
                      )}
                      {index > 0 && (
                        <Button
                          type="button"
                          sx={{
                            color: 'var(--primary)',
                            borderRadius: '4px',
                            minWidth: '40px',
                            minHeight: '40px',
                          }}
                          onClick={() => {
                            deletePostScript(index);
                          }}
                        >
                          <span className={styles.deleteScript} />
                        </Button>
                      )}
                    </div>
                    <ErrorMessage
                      name={`postBackupScripts.${index}.script`}
                      errors={formState.errors}
                      render={({message}) => (
                        <span className={styles.inputErrorMessage}>
                          {message}
                        </span>
                      )}
                    />
                  </div>
                ))}
              </>
            ) : null}

            <Divider
              sx={{backgroundColor: 'var(--border)', margin: '28px 0 14px 0'}}
            />
            <div className={styles.createTaskButtonsContainer}>
              <Button
                sx={cancelButton}
                onClick={() => props.setIsEditTaskModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={Boolean(Object.keys(formState.errors).length)}
                sx={{
                  ...warningModalButton,
                  '&:disabled': {background: 'var(--disabled-bg)', color: 'var(--text-secondary)'},
                }}
                onClick={() => {
                  setShouldSaveChangesModalOpen(true);
                }}
              >
                Save
              </Button>
            </div>
          </form>
        </Box>
      </Modal>

      <ShouldSaveChanges
        isNestedModalOpen={shouldSaveChangesModalOpen}
        setIsNestedModalOpen={setShouldSaveChangesModalOpen}
        manifestTaskFormId={editManifestTaskFormId}
        loading={isEditingManifestLoading}
        disabled={isEditingManifestLoading}
        confirmButtonText={'Yes, save'}
      />

      <Snackbar
        anchorOrigin={{
          vertical: editManifestTaskSuccessVertical,
          horizontal: editManifestTaskSuccessHorizontal,
        }}
        autoHideDuration={2500}
        open={isEditManifestTaskSuccessOpen}
        onClose={handleSnackbarClose}
        key={'editManifestTaskSuccess'}
      >
        <Alert icon={false} sx={snackbarSuccessAlert}>
          <Box sx={snackbarAlertContent}>
            <SuccessNotification />
            The data was successfully saved
            <span onClick={handleSnackbarClose}>
              <CloseIcon />
            </span>
          </Box>
        </Alert>
      </Snackbar>

      {editManifestTaskErrorMessage ? (
        <Snackbar
          anchorOrigin={{
            vertical: editManifestTaskErrorVertical,
            horizontal: editManifestTaskErrorHorizontal,
          }}
          autoHideDuration={3500}
          open={isEditManifestTaskErrorOpen}
          onClose={handleSnackbarClose}
          key={'editManifestTaskError'}
        >
          <Alert icon={false} sx={snackbarErrorAlert}>
            <Box sx={snackbarAlertContent}>
              <ErrorNotification />
              {editManifestTaskErrorMessage}
              <span onClick={handleSnackbarClose}>
                <CloseIcon />
              </span>
            </Box>
          </Alert>
        </Snackbar>
      ) : null}
    </>
  );
};

export default ManifestEditTask;
